/**
 * Точка входа store: корневой store, middleware и хук подписки.
 *
 * @module store
 */

import { useEffect, useRef, useState } from 'react';
import { createSelector } from 'reselect';

import type { AppState } from './app/types';
import { rootStore } from './createStore';
import type { ProfileState } from './profile';
import type { RegistrationSurveyState } from './registrationSurvey';
import type { RootState } from './store.types';

export { setMeta } from './store.utils';
export type { MetaState } from './store.utils';

export { STATUS_ERROR, STATUS_START, STATUS_SUCCESS } from './store.constants';

export type { RootAction, RootDispatch, RootState } from './store.types';

/**
 * Селектор слайса app (мемоизация через reselect).
 */
export const selectAppState = createSelector([(state: RootState) => state.app], (appSlice): AppState => appSlice);

/**
 * Селектор слайса profile.
 */
export const selectProfileState = createSelector([(state: RootState) => state.profile], (slice): ProfileState => slice);

/**
 * Селектор слайса registrationSurvey.
 */
export const selectRegistrationSurveyState = createSelector(
  [(state: RootState) => state.registrationSurvey],
  (slice): RegistrationSurveyState => slice,
);

/**
 * Хук подписки на корневой store по селектору.
 *
 * @template T — тип выбранного фрагмента состояния
 * @param {(state: RootState) => T} selector — функция выборки из корня
 * @returns {T} актуальное значение
 */
export function useRootState<T>(selector: (state: RootState) => T): T {
  const [value, setValue] = useState<T>(() => selector(rootStore.getState()));
  const selectorRef = useRef(selector);
  const isMountedRef = useRef(true);
  selectorRef.current = selector;

  useEffect(() => {
    isMountedRef.current = true;
    const unsubscribe = rootStore.subscribe(() => {
      if (!isMountedRef.current) {
        return;
      }
      setValue(selectorRef.current(rootStore.getState()));
    });
    return () => {
      isMountedRef.current = false;
      unsubscribe();
    };
  }, []);

  return value;
}

/**
 * Подписка на слайс app через произвольный селектор.
 *
 * @template T
 * @param {(state: AppState) => T} selector — выборка из слайса app
 * @returns {T}
 */
export function useAppState<T>(selector: (state: AppState) => T): T {
  return useRootState((root) => selector(selectAppState(root)));
}

/**
 * Подписка на слайс profile.
 *
 * @template T
 * @param {(state: ProfileState) => T} selector — выборка из слайса
 * @returns {T}
 */
export function useProfileState<T>(selector: (state: ProfileState) => T): T {
  return useRootState((root) => selector(selectProfileState(root)));
}

/**
 * Подписка на слайс registrationSurvey.
 *
 * @template T
 * @param {(state: RegistrationSurveyState) => T} selector — выборка из слайса
 * @returns {T}
 */
export function useRegistrationSurveyState<T>(selector: (state: RegistrationSurveyState) => T): T {
  return useRootState((root) => selector(selectRegistrationSurveyState(root)));
}

export {
  selectFetchProfileSuccess,
  selectProfileData,
  selectProfileDeleteRequestTimestamp,
  selectProfileError,
  selectProfileFetchStatuses,
  selectProfileFulfill,
  selectProfileInitials,
  selectProfileIsDeleted,
  selectProfileLoading,
  selectProfileMeta,
  selectProfileReady,
} from './profile';

export {
  selectFetchRegistrationSurveyStatusComplete,
  selectFetchRegistrationSurveyStatusErrored,
  selectFetchRegistrationSurveyStatusLoading,
  selectRegistrationSurveyCompleted,
  selectRegistrationSurveyData,
  selectRegistrationSurveyError,
  selectRegistrationSurveyIsFilled,
  selectRegistrationSurveyMeta,
  selectRegistrationSurveyNeeded,
  selectRegistrationSurveyResponseMalformed,
} from './registrationSurvey';

export { rootStore } from './createStore';
